/* eslint-disable react-hooks/exhaustive-deps */
import './PathTable.less';

import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Card, Spin, Tag, Tooltip } from 'antd';
import axios from 'axios';
import React, { Fragment, useEffect } from 'react';

import { OptionItemsPolicy, OptionItemsPolicyItem } from '../../models/Option';
import { PathSearchFilter, RatePath } from '../../models/Path';
import pathService from '../../services/pathService';

interface IPathTableProps {
  filter: PathSearchFilter;
  searchType: string;
  title: String;
}

const getFieldValue = (value?: string) => {
  return value ? value : <>&mdash;&mdash;&mdash;&mdash;</>;
};

const parseItemsPolicy = (raw?: string): OptionItemsPolicy[] | null => {
  if (!raw) return null;
  try {
    let parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? (parsed as OptionItemsPolicy[]) : null;
  } catch {
    return null;
  }
};

const formatPolicyItem = (item: OptionItemsPolicyItem): string => {
  return item.weight != null ? `${item.weight} ${item.unit}` : item.unit;
};

const policyItemSortValue = (item: OptionItemsPolicyItem): number => {
  const n = Number(item.weight);
  return Number.isFinite(n) ? n : Infinity;
};

type PolicyMatch = { priority: number; sortValue: number; value: string };

const getPolicyMatch = (policies: OptionItemsPolicy[]): PolicyMatch | null => {
  const items = policies
    .flatMap((policy) => policy.items ?? [])
    .filter((item): item is OptionItemsPolicyItem =>
      Boolean(item && item.unit)
    );

  if (items.length === 0) return null;

  const tireItem = items.find(
    (item) => item.unit === 'Tires' || item.unit === 'Baby Seats'
  );
  if (tireItem) {
    return {
      priority: 1,
      sortValue: policyItemSortValue(tireItem),
      value: formatPolicyItem(tireItem),
    };
  }

  const weightItem = items.find(
    (item) => item.unit === 'Lbs' || item.unit === 'Kgs'
  );
  if (weightItem) {
    return {
      priority: 0,
      sortValue: policyItemSortValue(weightItem),
      value: formatPolicyItem(weightItem),
    };
  }

  return null;
};

const isStricterPolicyMatch = (a: PolicyMatch, b: PolicyMatch): boolean => {
  if (a.priority !== b.priority) return a.priority > b.priority;
  return a.sortValue < b.sortValue;
};

const hasNoItemsPolicy = (policies: OptionItemsPolicy[]): boolean => {
  const items = policies.flatMap((policy) => policy.items ?? []);
  return items.length === 0;
};

const getPathItemsSummary = (path: RatePath[]): string | null => {
  let best: PolicyMatch | null = null;

  for (const leg of path) {
    if (leg.rateId == null) continue;

    const policies = parseItemsPolicy(leg.itemsPolicy);
    if (!policies) continue;

    if (hasNoItemsPolicy(policies)) return null;

    const match = getPolicyMatch(policies);
    if (!match) continue;

    if (!best || isStricterPolicyMatch(match, best)) {
      best = match;
    }
  }

  return best?.value ?? null;
};

const getRatePathItemsValue = (ratePath: RatePath): string | null => {
  const policies = parseItemsPolicy(ratePath.itemsPolicy);
  if (!policies) return null;

  return getPolicyMatch(policies)?.value ?? null;
};

const getCarrierName = (ratePath: RatePath) => {
  if (ratePath.carriers?.length) {
    return ratePath.carriers.join(' > ');
  }

  return getFieldValue(ratePath.carrierName);
};

const getLegSegmentsForShipTypes = (ratePath: RatePath[]): RatePath[] => {
  const pathSegments = ratePath.filter((p) => p.rateId != null);
  return pathSegments.length > 0 ? pathSegments : ratePath;
};

const getPathTotalDistance = (path: RatePath[]): number | null => {
  const legs = path.filter((p) => p.rateId != null && p.distance != null);
  if (legs.length === 0) return null;
  return legs.reduce((sum, p) => sum + p.distance!, 0);
};

const getPathTotalOriginalRate = (path: RatePath[]): number | null => {
  const legs = path.filter(
    (p) => p.rateId != null && (p.originalRate != null || p.rate != null)
  );
  if (legs.length === 0) return null;
  return legs.reduce((sum, p) => sum + (p.originalRate ?? p.rate)!, 0);
};

const getPathTotalWeightedRate = (path: RatePath[]): number | null => {
  const legs = path.filter((p) => p.rateId != null && p.rate != null);
  if (legs.length === 0) return null;
  return legs.reduce((sum, p) => sum + p.rate!, 0);
};

const getShipTypeLabel = (
  ratePath: RatePath,
  type: 'origin' | 'destination'
): string | undefined => {
  if (type === 'origin') {
    return ratePath.originShipTypeName ?? ratePath.shipType;
  }
  return ratePath.destinationShipTypeName ?? ratePath.destinationShipType;
};

const savePath = async (path: RatePath[]) => {
  //console.log('Save Path', path);
  // get rateIds from each member of path, skipping the ones without a rateId
  const rateIds = path.map((p) => p.rateId).filter((r) => r);
  //console.log('Rate Ids', rateIds);

  // queryClient and all that is a hot mess and doesn't want to work here for convoluted reasons so we're just calling Axios directly for now
  // JS doesn't think that this is a react component so it won't let us use hooks here
  const result = await axios.post(process.env.REACT_APP_API_URL + '/trip', {
    rateIds: rateIds,
  });
  if (result.status !== 201) {
    console.error('Failed to save trip', result);
    return;
  } else {
    alert('Added trip to your list');
  }
};

const PathTable: React.FC<IPathTableProps> = ({
  filter,
  searchType,
  title,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [paths, setPaths] = React.useState<RatePath[][]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const runSearch = async () => {
    setPaths([]);
    setIsLoading(true);

    const newFilter = { ...filter, t: searchType };
    const paths = await pathService.getPaths(newFilter);

    setPaths(paths);
    setIsLoading(false);
  };

  useEffect(() => {
    // on filter or searchtype change run the search with appropriate parameters
    runSearch();
  }, [filter, searchType]);

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ flex: 1 }}>
          <h3>{title}</h3>
        </div>
        <div style={{ flex: 1, textAlign: 'right' }}>
          {paths.length > 0 && (
            <h3>
              {paths.length} Path{paths.length > 1 ? 's' : ''} Found
            </h3>
          )}
        </div>
      </div>
      {isLoading && <Spin size="large" style={{ width: '100%' }} />}
      {paths.length > 0 && (
        <table className="path-table">
          <thead>
            <tr>
              <th>Carrier</th>
              <th>Origin</th>
              <th>Org Type</th>
              <th>Destination</th>
              <th>Dest Type</th>
              <th>Mode</th>
              <th>Items</th>
              <th>Distance (kms)</th>
              <th>Transit</th>
              <th>Rate</th>
            </tr>
          </thead>
          {paths.map((path, index) => (
            <>
              <Fragment key={index}>
                <tbody className="path-table__body">
                  {path
                    .map((ratePath, originalIndex) => ({
                      originalIndex,
                      ratePath,
                    }))
                    .filter(
                      ({ originalIndex }) =>
                        !(path.length === 2 && originalIndex === 1)
                    )
                    .map(({ originalIndex, ratePath }, displayIndex) => (
                      <tr
                        key={originalIndex}
                        className={ratePath.preferred ? 'preferred' : ''}
                      >
                        <td>{getCarrierName(ratePath)}</td>
                        <td>{getFieldValue(ratePath.origin)}</td>
                        <td>
                          {getFieldValue(
                            originalIndex === 0
                              ? getShipTypeLabel(
                                  getLegSegmentsForShipTypes(path)[0],
                                  'origin'
                                )
                              : getShipTypeLabel(ratePath, 'origin')
                          )}
                        </td>
                        <td>{getFieldValue(ratePath.destination)}</td>
                        <td>
                          {getFieldValue(
                            originalIndex === 0
                              ? getShipTypeLabel(
                                  getLegSegmentsForShipTypes(path).at(-1)!,
                                  'destination'
                                )
                              : getShipTypeLabel(ratePath, 'destination')
                          )}
                        </td>
                        <td>
                          {displayIndex === 0
                            ? getFieldValue(
                                Array.from(new Set(ratePath.modes)).join(', ')
                              )
                            : getFieldValue(ratePath.modeName)}
                        </td>
                        <td>
                          {originalIndex === 0
                            ? getFieldValue(
                                getPathItemsSummary(path) ?? undefined
                              )
                            : getFieldValue(
                                getRatePathItemsValue(ratePath) ?? undefined
                              )}
                        </td>
                        <td>
                          {displayIndex === 0
                            ? getFieldValue(
                                (() => {
                                  const total = getPathTotalDistance(path);
                                  return total != null
                                    ? Math.ceil(total).toLocaleString()
                                    : undefined;
                                })()
                              )
                            : getFieldValue(
                                ratePath.distance
                                  ? Math.ceil(
                                      ratePath.distance
                                    ).toLocaleString()
                                  : undefined
                              )}
                        </td>
                        <td>
                          {getFieldValue(
                            ratePath.transitDays
                              ? ratePath.transitDays + ' days'
                              : undefined
                          )}
                        </td>
                        <td>
                          {displayIndex === 0 ? (
                            (() => {
                              const displayTotal =
                                getPathTotalOriginalRate(path);
                              const weightedTotal =
                                getPathTotalWeightedRate(path);
                              if (displayTotal == null) {
                                return <>&mdash;&mdash;&mdash;&mdash;</>;
                              }
                              const tag = (
                                <Tag color="#358F56">
                                  {displayTotal.toFixed(2)}
                                </Tag>
                              );
                              return weightedTotal != null ? (
                                <Tooltip title={weightedTotal.toFixed(2)}>
                                  {tag}
                                </Tooltip>
                              ) : (
                                tag
                              );
                            })()
                          ) : ratePath.originalRate != null ||
                            ratePath.rate != null ? (
                            ratePath.rate != null ? (
                              <Tooltip title={ratePath.rate.toFixed(2)}>
                                {(ratePath.originalRate ??
                                  ratePath.rate)!.toFixed(2)}
                              </Tooltip>
                            ) : (
                              (ratePath.originalRate ?? ratePath.rate)!.toFixed(
                                2
                              )
                            )
                          ) : (
                            <>&mdash;&mdash;&mdash;&mdash;</>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
                <tbody className="path-table__separator">
                  <tr>
                    <td colSpan={10}></td>
                  </tr>
                </tbody>
                <div style={{ marginBottom: 20, width: '100%' }}>
                  <Button
                    icon={<PlusCircleOutlined />}
                    size="small"
                    type="primary"
                    onClick={() => savePath(path)}
                  >
                    Save as Trip
                  </Button>
                </div>
              </Fragment>
            </>
          ))}
        </table>
      )}
    </Card>
  );
};

export default PathTable;
